import { AccessGuids, ConfigSystemParams } from 'src/app/core/consts';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FeatureAccessResetService, FeatureAccessResettable } from 'src/app/core/services/utelize/access/feature-access-reset-service';
import { Injectable } from '@angular/core';
import { LicencesCompanyAllocatedService } from 'src/app/core/services/utelize/system/licence/licences-company-allocated-service';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

@Injectable({ providedIn: 'root' })

export class SignalsAccessService implements FeatureAccessResettable {

  private featureEnabledSignals: boolean=false;
  private featureLoaded        : boolean=false;
  private inflightInit         : Promise<void> | undefined;

  constructor(
    private authenticationService          : AuthenticationService,
    private componentAccessUserService     : ComponentAccessUserService,
    private configSystemService            : ConfigSystemService,
    private exceptionsService              : ExceptionsService,
    private featureAccessResetService      : FeatureAccessResetService,
    private licencesCompanyAllocatedService: LicencesCompanyAllocatedService
  ) {
    this.featureAccessResetService.register(this);
  }

  //#region Visibility

  public canShow(): boolean {
    try {
      const menuShown: boolean=this.configSystemService.getValueAsBoolean(ConfigSystemParams.MenuShowSignals, false);

      if (!menuShown)
        return false;

      return this.componentAccessUserService.hasAccessGuid(AccessGuids.MenuItemSignals, true)
             && this.featureEnabledSignals;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowBreachExposure(): boolean {
    try {
      return this.canShowChild(ConfigSystemParams.MenuShowSignalsBreachExposure, AccessGuids.MenuItemSignalsBreachExposure);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowCorporateHealth(): boolean {
    try {
      return this.canShowChild(ConfigSystemParams.MenuShowSignalsCorporateHealth, AccessGuids.MenuItemSignalsCorporateHealth);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowDashboard(): boolean {
    try {
      return this.canShowChild(ConfigSystemParams.MenuShowSignalsDashboard, AccessGuids.MenuItemSignalsDashboard);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowDeviceVulnerabilities(): boolean {
    try {
      return this.canShowChild(ConfigSystemParams.MenuShowSignalsDeviceVulnerabilities, AccessGuids.MenuItemSignalsDeviceVulnerabilities);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRisk(): boolean {
    try {
      return this.canShowChild(ConfigSystemParams.MenuShowSignalsTravelRisk, AccessGuids.MenuItemSignalsTravelRisk);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskAdvisories(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskAdvisories, AccessGuids.MenuItemSignalsTravelRiskAdvisories);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskAlerts(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskAlerts, AccessGuids.MenuItemSignalsTravelRiskAlerts);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskCityReports(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskCityReports, AccessGuids.MenuItemSignalsTravelRiskCityReports);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskComparison(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskComparison, AccessGuids.MenuItemSignalsTravelRiskComparison);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskCountryLevels(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskCountryRiskLevels, AccessGuids.MenuItemSignalsTravelRiskCountryRiskLevels);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskCountryReports(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskCountryReports, AccessGuids.MenuItemSignalsTravelRiskCountryReports);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskTripBrief(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskTripBrief, AccessGuids.MenuItemSignalsTravelRiskTripBrief);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public canShowTravelRiskExposure(): boolean {
    try {
      return this.canShowTravelRiskChild(ConfigSystemParams.MenuShowSignalsTravelRiskExposure, AccessGuids.MenuItemSignalsTravelRiskExposure);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  //#endregion

  //#region Lifecycle

  public async ensureInitialised(): Promise<void> {
    try {
      if (this.featureLoaded)
        return;

      if (this.inflightInit) {
        await this.inflightInit;
        return;
      }

      this.inflightInit=this.loadFeatureFlag();
      await this.inflightInit;
      this.inflightInit=undefined;
    }
    catch(e) {
      this.inflightInit=undefined;
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public isFeatureEnabled(): boolean {
    try {
      return this.featureEnabledSignals;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public resetFeatureCache(): void {
    try {
      this.featureEnabledSignals=false;
      this.featureLoaded        =false;
      this.inflightInit         =undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Private

  private canShowChild(configParam: string, accessGuid: string): boolean {
    try {
      if (!this.canShow())
        return false;

      const menuShown: boolean=this.configSystemService.getValueAsBoolean(configParam, false);

      if (!menuShown)
        return false;

      return this.componentAccessUserService.hasAccessGuid(accessGuid, true);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private canShowTravelRiskChild(configParam: string, accessGuid: string): boolean {
    try {
      if (!this.canShowTravelRisk())
        return false;

      const menuShown: boolean=this.configSystemService.getValueAsBoolean(configParam, false);

      if (!menuShown)
        return false;

      return this.componentAccessUserService.hasAccessGuid(accessGuid, true);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private async loadFeatureFlag(): Promise<void> {
    try {
      const curUser  : SystemUserDTO=this.authenticationService.currentUserObject();
      const companyId: number       =curUser?.companyId ?? -1;

      const data=await firstValueFrom(this.licencesCompanyAllocatedService.companyHasLicenceFeatureByGuid(companyId, AccessGuids.ChargeableFeatureSignals)
                       .pipe(
                         catchError((error) => {
                           return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
                         })
                       )
                      );

      this.featureEnabledSignals=data.result;
      this.featureLoaded        =true;
    }
    catch(e) {
      this.featureEnabledSignals=false;
      this.featureLoaded        =true;

      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
